/**
 * Axios instance for authenticated admin requests.
 * Sends Bearer token + X-Tenant-ID header.
 *
 * Ported from flowbuilder/src/lib/api.ts with the VSCode adapter and refresh queue
 * removed (out of scope for the Forms standalone slice).
 */
import axios, { AxiosError } from 'axios';

const STORAGE_TOKEN_KEY = 'mks_forms_token';

function getTenantId(): string {
  if (typeof window === 'undefined') return 'staff';
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const subdomain = parts[0];
  if (
    !subdomain ||
    subdomain === 'localhost' ||
    subdomain === 'app' ||
    subdomain === 'www' ||
    subdomain === 'forms' ||
    subdomain.includes(':')
  ) {
    return 'staff';
  }
  if (parts.length >= 2) return subdomain;
  return 'staff';
}

const resolveBaseURL = (): string => {
  const env = (import.meta as any).env ?? {};
  if (env.DEV) return '/api/v1';
  return env.VITE_API_URL ? `${env.VITE_API_URL}/api/v1` : '/api/v1';
};

export const api = axios.create({
  baseURL: resolveBaseURL(),
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(STORAGE_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['X-Tenant-ID'] = getTenantId();
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(STORAGE_TOKEN_KEY);
      localStorage.removeItem('mks_forms_user');
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

export default api;

export function setAuthToken(token: string | null): void {
  if (token) localStorage.setItem(STORAGE_TOKEN_KEY, token);
  else localStorage.removeItem(STORAGE_TOKEN_KEY);
}

export function clearAuthToken(): void {
  localStorage.removeItem(STORAGE_TOKEN_KEY);
}
